<template>
  <!-- „So funktioniert's“ (Arnold, 02.11.2026, visual plan TK8PVZg3BL3AjnduVBnFsw): the video in
       the user's language (UX3), muted (it has no sound) and starting by itself unless the system
       asks for reduced motion; below it the eight chapters, each with its caption as text, so
       screen readers and zoom get what the picture says. A load failure offers „Erneut laden“ and
       points to the help (?) (CA1). Mounted once in Layouts/Default.vue on planner pages. -->
  <Modal
    :show="plannerVideoOpen"
    :title="$t('planner.how_it_works.title')"
    :width="960"
    body-class="p-0! md:p-0!"
    @close="closePlannerVideo"
  >
    <div class="flex flex-col" data-test="planner-video-dialog">
      <div
        v-if="failed"
        class="flex flex-col items-start gap-2 px-5 py-6 text-sm"
        data-test="planner-video-error"
      >
        <strong
          class="font-semibold text-fg"
          v-text="$t('planner.how_it_works.error')"
        />
        <span
          class="text-fg-muted"
          v-text="$t('planner.how_it_works.error_hint')"
        />
        <Button
          variant="outline"
          data-test="planner-video-retry"
          @click="failed = false"
          >{{ $t('planner.how_it_works.retry') }}</Button
        >
      </div>
      <video
        v-else
        ref="player"
        class="mx-auto block aspect-video w-full max-w-[calc((100vh-26rem)*16/9)] bg-(--ds-color-grey-900)"
        :src="source"
        :autoplay="!reducedMotion"
        muted
        playsinline
        controls
        preload="metadata"
        data-test="planner-video"
        @error="failed = true"
        @timeupdate="time = $event.target.currentTime"
      />
      <!-- R10: the modal never outgrows the window — eight chapters in two rows of four, the
           video gives up height on short screens. -->
      <section class="flex flex-col gap-2 px-4 pt-3 pb-4">
        <h3
          class="text-xs font-semibold tracking-wide text-fg-muted uppercase"
          v-text="$t('planner.how_it_works.chapters')"
        />
        <ol class="grid grid-cols-2 gap-1 lg:grid-cols-4">
          <li v-for="chapter in chapters" :key="chapter.page" class="flex">
            <button
              type="button"
              class="flex w-full items-start gap-2 rounded-control px-2 py-1.5 text-left text-[13px] leading-snug transition-colors hover:bg-surface-violet focus-visible:outline-2 focus-visible:outline-focus"
              :class="chapter.page === current ? 'bg-surface-violet' : ''"
              :aria-current="chapter.page === current ? 'true' : undefined"
              :disabled="failed"
              data-test="planner-video-chapter"
              @click="jump(chapter.start)"
            >
              <span
                class="w-8 flex-none pt-px text-xs text-fg-muted tabular-nums"
                v-text="formatVideoDuration(chapter.start)"
              />
              <span class="flex min-w-0 flex-col gap-0.5">
                <strong
                  class="font-semibold text-fg"
                  v-text="$t(`common.${chapter.page}`)"
                />
                <span
                  class="text-fg-muted"
                  v-text="$t(`planner.how_it_works.caption_${chapter.page}`)"
                />
              </span>
            </button>
          </li>
        </ol>
      </section>
    </div>
  </Modal>
</template>

<script setup>
import Modal from '@/Components/Modal.vue'
import Button from '@/Components/common/Button.vue'
import {
  closePlannerVideo,
  currentPlannerVideo,
  formatVideoDuration,
  openPlannerVideoFromUrl,
  plannerVideoOpen,
  plannerVideoSource,
} from '@/composables/plannerVideo'
import { router } from '@inertiajs/vue3'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useTemplateRef,
  watch,
} from 'vue'

const player = useTemplateRef('player')
const failed = ref(false)
const time = ref(0)
const source = ref(null)
const chapters = ref([])
const reducedMotion = ref(false)

/** The chapter the video is in. */
const current = computed(
  () =>
    chapters.value.findLast((chapter) => chapter.start <= time.value)?.page ??
    null,
)

/** Language, address and motion setting are read on every opening (the page may have changed). */
watch(
  plannerVideoOpen,
  (open) => {
    if (!open) {
      return
    }
    failed.value = false
    time.value = 0
    source.value = plannerVideoSource()
    chapters.value = currentPlannerVideo().chapters
    reducedMotion.value =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true
  },
  { immediate: true },
)

async function jump(start) {
  await nextTick()
  if (!player.value) {
    return
  }
  player.value.currentTime = start
  time.value = start
  player.value.play()?.catch(() => {})
}

let stopListening = null

onMounted(() => {
  openPlannerVideoFromUrl()
  // The layout stays mounted across Inertia visits, so ?video=1 is checked on every visit.
  stopListening = router.on('navigate', openPlannerVideoFromUrl)
})

onBeforeUnmount(() => {
  stopListening?.()
  closePlannerVideo()
})
</script>
